import { Component, ElementRef, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { BaseControlValueAccessor } from "../base_control_value_accessor/base";
import { AutoSearchService } from "./auto-search.service";
import { BehaviorSubject, map, of, Subject } from "rxjs";
import { BackendListModel } from "../../../models/common/list.model";

@Component({
  selector: 'app-auto-search',
  templateUrl: './auto-search.component.html',
  styleUrls: ['../form-field.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AutoSearchComponent),
    }
  ]
})
export class AutoSearchComponent extends BaseControlValueAccessor implements OnInit {

  @Input()
  idField: string = 'id';

  @Input()
  displayField: string = null;

  @Input()
  retrieveUrl: string = null;

  @Input()
  retrieveUrlTransforms: Object = {};

  @Input()
  listUrl: string = null;

  @Input()
  listUrlTransforms: Object = {};

  @Input()
  optionsPageNum = 1;

  @Input()
  optionsPageSize = 10;

  @Input()
  multiple = false;

  @Input()
  trim = false

  @Input()
  setSearchValue = false

  @Input()
  defaultDisplay = '-';

  @Input()
  getDisplay(item) {

    if (this.displayField == null)
      return item;

    if (item == null)
      return this.defaultDisplay;

    if (typeof item == 'object' && this.displayField in item) {
      return item[this.displayField];
    } else if (this.setSearchValue) {
      return item
    }
    return this.defaultDisplay;
  }

  @Input()
  options: Array<any> = null;
  loading = false;
  searchBoxText = '';
  selectedIndex = -1;
  showOptions = false;

  @ViewChild('searchBox')
  searchBox: ElementRef;

  override get data() {
    return this._data;
  }

  override set data(val) {
    if (this._data != val) {
      this._data = val;
      this.validate(val);
      this.onChange(val);
      this.initializeField();
    }
  }

  isFocused = true;

  constructor(private autoSearchService: AutoSearchService) {
    super();
  }

  ngOnInit(): void {

  }

  initializeField() {
    if ((this.options == null || this.options.length == 0) && (this._data != null && this._data != '')) {
      this.options = [this._data];
      this.selectItem(0);
    }
  }

  blackHole(event) {
    const ENTER = 13;

    if (event.keyCode == ENTER) {
      event.preventDefault();
    }
  }

  onSearchKeyUp(event) {
    event.preventDefault();

    const UP_ARROW = 38;
    const DOWN_ARROW = 40;
    const ENTER = 13;

    if (event.keyCode == UP_ARROW) {
      if (this.selectedIndex > 0) {
        if (this.multiple) {
          this.selectedIndex--;
        } else {
          this.selectItem(this.selectedIndex - 1, true);
        }
        return;
      }
    }

    if (event.keyCode == DOWN_ARROW) {
      if (this.selectedIndex < this.options.length - 1) {
        if (this.multiple) {
          this.selectedIndex++;
        } else {
          this.selectItem(this.selectedIndex + 1, true);
        }
        return;
      }
    }

    if (event.keyCode == ENTER) {
      this.showOptions = false;
      if (this.multiple) {
        this.selectItem(this.selectedIndex, false);

      }
      return;
    }

    const searchBoxText = event.target.value;
    this.loading = true;
    this.showOptions = true;
    if (this.setSearchValue) {
      this.data = searchBoxText;
    } else if (!this.multiple) {
      this.data = null;
    }


    this.getOptionList(searchBoxText).subscribe((data) => {
      if (!this.setSearchValue && !this.multiple) {
        this.data = null;
      }
      this.options = data.results;
      this.loading = false;
    });
  }

  getOptionList(searchTerm) {

    if (this.listUrl == null) {

      if (this.options == null) {
        console.error("No options provided");
      }

      let response: BackendListModel<any> = {
        count: this.options.length,
        next: null,
        previous: null,
        results: this.options
      }

      return of(response);
    }

    let transforms = { ...this.retrieveUrlTransforms }
    transforms['search'] = searchTerm;

    let url = new URL(this.applyUrlTransforms(this.listUrl, transforms));
    url.searchParams.append('page', String(this.optionsPageNum));
    url.searchParams.append('page_size', String(this.optionsPageSize));

    return this.autoSearchService.listObjects(String(url));
  }

  selectItem(itemIdx, showOptions = false) {

    let selectedItem = JSON.parse(JSON.stringify(this.options[itemIdx]));

    if (this.multiple) {
      if (this.data == null || this.data == '') {
        this.data = [];
      }
      if (!this.data.filter(item => item.id == selectedItem.id).length) {
        this.data.push(selectedItem);
        this.onChange(this.data);
        this.clearAndFocusOnSearch();

      }
    } else {
      if (itemIdx >= 0 && itemIdx < this.options.length) {
        this.selectedIndex = itemIdx;
        this.data = selectedItem;
        this.searchBoxText = this.getDisplay(this.data);
        this.showOptions = showOptions;
      }
    }
  }

  onFocusOut(event) {

    this.isFocused = false;
    setTimeout(() => {
      if (!this.isFocused) {
        this.showOptions = false;
        if (this.selectedIndex != -1 && this.options.length && !this.multiple) {
          this.selectItem(this.selectedIndex);
        }
      }
    }, 500)
  }

  onFocusIn(event) {
    this.isFocused = true
    this.showOptions = true;

    if (this.multiple && event.target != this.searchBox.nativeElement) {
      this.clearAndFocusOnSearch();
    }
  }

  removeFromList(index) {
    this.data.splice(index, 1);
    this.onChange(this.data);
  }

  clearAndFocusOnSearch() {
    this.searchBoxText = '';
    this.searchBox.nativeElement.focus();
    this.searchBox.nativeElement.value = '';
  }
}
